// src/components/Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

function Navbar({ user, onSignIn, onSignOut, onNavigate, currentPage }) {
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toolsDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(event.target)) {
        setIsToolsDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && !event.target.closest('[data-hamburger]')) {
        setIsMobileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMainNavigate = (page) => {
    if (typeof onNavigate === 'function') onNavigate(page);
    setIsToolsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const toolItems = [
    { key: 'imageConverter', label: 'Image Converter' },
    { key: 'listingGenerator', label: 'Listing Generator' },
    { key: 'popCultureImageGen', label: 'Pop Culture Image Gen' },
    { key: 'productImageGen', label: 'Product Image Gen' },
    { key: 'amazonCalculator', label: 'Amazon FBA/FBM Calculator' },
  ];

  const allToolPages = toolItems.map(t => t.key);
  const isToolPageActive = allToolPages.includes(currentPage);

  const navBtnClass = "px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200";
  const menuItemClass = "block w-full text-left px-4 py-3 text-sm transition-colors";

  return (
    <nav className="bg-slate-900/80 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <span
            className="font-bold text-xl sm:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400 cursor-pointer hover:from-indigo-300 hover:to-cyan-300 transition-colors"
            onClick={() => handleMainNavigate('home')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleMainNavigate('home')}
            aria-label="Go to homepage"
          >
            JustAutomateIt
          </span>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-2">
            <button
              onClick={() => handleMainNavigate('home')}
              aria-current={currentPage === 'home' ? 'page' : undefined}
              className={`${navBtnClass} ${currentPage === 'home' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-300 hover:bg-slate-800 hover:text-white'}`}
            >
              Home
            </button>

            {/* Tools Dropdown */}
            <div className="relative" ref={toolsDropdownRef}>
              <button
                onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
                type="button"
                className={`${navBtnClass} flex items-center gap-1 ${isToolPageActive ? 'bg-slate-800 text-white' : 'text-gray-300 hover:bg-slate-800 hover:text-white'}`}
                aria-haspopup="true"
                aria-expanded={isToolsDropdownOpen}
              >
                Tools
                <ChevronDown size={14} className={`transition-transform duration-200 ${isToolsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isToolsDropdownOpen && (
                <div
                  className="origin-top-right absolute right-0 mt-2 w-56 rounded-xl shadow-xl bg-slate-800 ring-1 ring-black ring-opacity-5 py-1 border border-slate-700 animate-fade-in"
                  role="menu"
                >
                  {toolItems.map(item => (
                    <button
                      key={item.key}
                      onClick={() => handleMainNavigate(item.key)}
                      aria-current={currentPage === item.key ? 'page' : undefined}
                      className={`${menuItemClass} ${currentPage === item.key ? 'bg-slate-700 text-white' : 'text-gray-300 hover:bg-slate-700 hover:text-white'}`}
                      role="menuitem"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <>
                <button
                  onClick={() => handleMainNavigate('account')}
                  aria-current={currentPage === 'account' ? 'page' : undefined}
                  className={`${navBtnClass} ${currentPage === 'account' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-300 hover:bg-slate-800 hover:text-white'}`}
                >
                  Account
                </button>
                <button
                  onClick={onSignOut}
                  className={`${navBtnClass} bg-red-500/80 hover:bg-red-600 text-white backdrop-blur-sm`}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => { if (typeof onSignIn === 'function') onSignIn('account'); }}
                className={`${navBtnClass} bg-indigo-600 hover:bg-indigo-700 text-white flex items-center space-x-2 shadow-md`}
              >
                <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="20px" height="20px"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" /><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" /><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" /><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l0.001-0.001l6.19,5.238C39.712,34.464,44,29.865,44,24C44,22.659,43.862,21.35,43.611,20.083z" /></svg>
                <span>Sign In</span>
              </button>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            data-hamburger
            className="md:hidden p-2.5 rounded-lg text-gray-300 hover:bg-slate-800 hover:text-white transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden border-t border-slate-700 bg-slate-900/95 backdrop-blur-md animate-fade-in">
          <div className="px-4 py-3 space-y-1">
            <button
              onClick={() => handleMainNavigate('home')}
              className={`block w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${currentPage === 'home' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-slate-800 hover:text-white'}`}
            >
              Home
            </button>

            {/* Tools section */}
            <div className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider">Tools</div>
            {toolItems.map(item => (
              <button
                key={item.key}
                onClick={() => handleMainNavigate(item.key)}
                className={`block w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${currentPage === item.key ? 'bg-slate-700 text-white' : 'text-gray-300 hover:bg-slate-800 hover:text-white'}`}
              >
                {item.label}
              </button>
            ))}

            {/* Divider */}
            <div className="border-t border-slate-700 my-2"></div>

            {user ? (
              <>
                <button
                  onClick={() => handleMainNavigate('account')}
                  className={`block w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${currentPage === 'account' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-slate-800 hover:text-white'}`}
                >
                  Account
                </button>
                <button
                  onClick={() => { onSignOut(); setIsMobileMenuOpen(false); }}
                  className="block w-full text-left px-4 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/20 transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => { if (typeof onSignIn === 'function') onSignIn('account'); setIsMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-3 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
              >
                Sign In with Google
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
